import { useBranchTheme } from '@/entities/Branch/@x/Permission';
import { useUserStore } from '@/entities/User/@x/Permission';
import { ERROR_MESSAGES } from '@/shared/config';
import type { AppAbility, AppRawRule, CaslAction, Subjects } from '@/shared/lib/casl';
import { detectSubjectType } from '@/shared/lib/casl';
import type { ExtractSubjectType } from '@casl/ability';
import { createMongoAbility } from '@casl/ability';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

const typedDetectSubjectType = detectSubjectType as (
  subject: Record<string, unknown>
) => ExtractSubjectType<Subjects>;

function createEmptyAbility(): AppAbility {
  return createMongoAbility([], { detectSubjectType: typedDetectSubjectType }) as AppAbility;
}

const SUPERUSER_RULES: AppRawRule[] = [{ action: 'manage', subject: 'all' }];

export const usePermissionStore = defineStore('permission', () => {
  const userStore = useUserStore();
  const { getCurrentSelectedBranch } = useBranchTheme();

  const selectedBranchId = useStorage<string | null>('selectedBranchId', null);

  const rules = ref<AppRawRule[]>([]);
  const version = ref<string | null>(null);
  const ability = ref<AppAbility>(createEmptyAbility());
  const error = ref<Error | null>(null);

  const isLoaded = computed(() => rules.value.length > 0);

  function buildAbility(): void {
    error.value = null;

    const user = userStore.user;
    if (!user) {
      rules.value = [];
      version.value = null;
      ability.value = createEmptyAbility();
      return;
    }

    if (userStore.isSuperuser) {
      rules.value = SUPERUSER_RULES;
      version.value = user.permissionVersion ?? null;
      ability.value = createMongoAbility(SUPERUSER_RULES, {
        detectSubjectType: typedDetectSubjectType,
      }) as AppAbility;
      return;
    }

    if (!user.memberships || user.memberships.length === 0) {
      rules.value = [];
      version.value = null;
      ability.value = createEmptyAbility();
      return;
    }

    const currentBranchId = getCurrentSelectedBranch();
    const membership = currentBranchId
      ? user.memberships.find((m) => m.branchId === Number(currentBranchId))
      : undefined;

    if (!membership) {
      error.value = new Error(ERROR_MESSAGES.SELECTED_BRANCH_NOT_FOUND_IN_MEMBERSHIPS);
      rules.value = [];
      version.value = null;
      ability.value = createEmptyAbility();
      return;
    }

    rules.value = membership.permissions;
    version.value = user.permissionVersion ?? null;
    ability.value = createMongoAbility(membership.permissions, {
      detectSubjectType: typedDetectSubjectType,
    }) as AppAbility;
  }

  watch(
    [
      () => userStore.user?.memberships,
      () => userStore.user?.permissionVersion,
      () => userStore.isSuperuser,
      selectedBranchId,
    ],
    buildAbility,
    { immediate: true }
  );

  function can(action: CaslAction, subject: Subjects): boolean {
    return ability.value.can(action, subject);
  }

  function cannot(action: CaslAction, subject: Subjects): boolean {
    return !can(action, subject);
  }

  function $reset(): void {
    rules.value = [];
    version.value = null;
    ability.value = createEmptyAbility();
    error.value = null;
  }

  return {
    rules,
    version,
    ability,
    error,
    isLoaded,
    buildAbility,
    can,
    cannot,
    $reset,
  };
});
